import React from 'react';
import { useMemo } from 'react';
import { DeleteButton } from '../../buttons/DeleteButton';

/**
 * Hook to conditionally add a delete column to the columns array
 * 
 * @param {Array} columns - Array of column definitions
 * @param {Object} options - Configuration options
 * @param {boolean} options.showDeleteColumn - Whether to show the delete column
 * @param {Function} options.onDelete - Callback when delete is confirmed, receives the row
 * @param {boolean|Function} options.disabled - Whether delete button should be disabled, or a function receiving the row
 * @param {Function} options.shouldHideForRow - Function that receives row and returns true if delete should be hidden
 * @param {string|Function} options.tooltipText - Delete button tooltip text, or a function receiving the row
 * @param {string} options.confirmMessage - Confirmation message (default: 'Are you sure you want to delete this row?')
 * @param {string} options.columnKey - Column key (default: 'delete')
 * @param {number} options.width - Column width (default: 60)
 * @param {string} options.iconColor - Color of delete icon (default: '#dc3545')
 * @param {string} options.icon - Material icon name (default: 'delete')
 * @returns {Array} Columns array with or without delete column appended
 */
export function useDeleteColumn(columns) {
  let {
    showDeleteColumn = false,
    onDelete,
    disabled = false,
    shouldHideForRow = () => false,
    tooltipText = null,
    confirmMessage = 'Are you sure you want to delete this row?',
    columnKey = 'delete',
    width = 60,
    iconColor = '#dc3545',
    icon = 'delete'
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return useMemo(() => {
    // If delete column should not be shown or onDelete is not provided, return columns as-is
    if (!showDeleteColumn || !onDelete) {
      return columns;
    }

    // Create delete column
    const deleteColumn = {
      key: columnKey,
      name: '',
      width,
      resizable: false,
      renderCell: _ref => {
        let {
          row
        } = _ref;
        if (shouldHideForRow(row)) return null;
        const isDisabled = typeof disabled === 'function' ? disabled(row) : disabled;
        const rowTooltipText = typeof tooltipText === 'function' ? tooltipText(row) : tooltipText;
        return /*#__PURE__*/React.createElement("div", {
          style: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }
        }, /*#__PURE__*/React.createElement(DeleteButton, {
          onClick: e => {
            e.stopPropagation();
            if (window.confirm(confirmMessage)) {
              onDelete(row);
            }
          },
          disabled: isDisabled,
          icon: icon,
          iconStyles: {
            color: iconColor
          },
          tooltipText: rowTooltipText,
          title: "Delete row"
        }));
      }
    };

    // Return columns with delete column appended
    return [...columns, deleteColumn];
  }, [columns, showDeleteColumn, onDelete, disabled, shouldHideForRow, tooltipText, confirmMessage, columnKey, width, iconColor, icon]);
}